import React, { useCallback, useState } from 'react'
import { HallType } from '@weblite/ts-action/hall'
import { Avatar, Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { getFileFromUser } from '@weblite/file'
import { ErrorOutlineIcon, WChannelIcon, WGroupIcon } from '@weblite/icons'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { SettingsSection } from '$common/settings-section'
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { MultilineInput, TextInput } from '$common/input'
import { Label } from '$common/label'
import { WithEditableAvatar } from '$common/with-editable-avatar'
// Localization
import { t } from '$i18n'
// Helper
import { nothing } from '$func/utils'
import { hallConstraints } from '$logic/hall/constant'
import {
  closeCreateHall,
  openAddHallMembers,
  openDefineHallAdvancedSettings,
} from '$logic/router'
import { getImageUrl } from '$func/imageUrl'
import { ProfilePictureMenu } from '$comp/modals/menus/profile-picture-menu'
import { showEditProfileDialog } from '$comp/modals/edit-profile-dialog'
import { uploadImage } from '$func/upload-image'
import { createHall } from '$logic/hall'
import { useCurrentWorkSpaceId } from '$comp/main/react-hook'
import { DEFAULT_WORKSPACE_ID } from '$logic/workspace'
import { useOnboarding } from '$logic/onboarding/react-hooks'
import { CommonOnboarding } from '$logic/onboarding/types'

const StyledAvatar = styled(Avatar)`
  width: 100px;
  height: 100px;
  margin: 0 auto;
`

interface Props {
  hallType: HallType
  tags?: string[]
}

export function CreateHall({ hallType, tags }: Props) {
  const currentWorkspaceId = useCurrentWorkSpaceId()
  const workspaceId =
    currentWorkspaceId === DEFAULT_WORKSPACE_ID ? undefined : currentWorkspaceId

  // TODO: IMPLEMENT LOGIC FOR UPDATE
  const [imageFileId, setImageFileId] = useState<string>()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')

  const { completeOnboarding } = useOnboarding(CommonOnboarding.CREATE_COURSE)

  const onConfirmHandle = useCallback(async () => {
    if (!title) return
    // TODO: Show Loading

    const roomId = await createHall({
      name: title,
      type: hallType,
      description,
      image: imageFileId,
      tags: tags || [],
      public: false,
      workspaceId: hallType === 'CHANNEL' ? workspaceId : undefined,
    })

    if (!roomId) return
    if (hallType === 'CHANNEL') {
      completeOnboarding()
      analite('CREATE_CHANNEL', {
        name: title,
        roomId,
        workspaceId,
      })
    }

    if (hallType === 'GROUP') {
      openAddHallMembers(roomId, true, true)
    } else openDefineHallAdvancedSettings(roomId, true)
  }, [
    imageFileId,
    title,
    description,
    tags,
    hallType,
    workspaceId,
    completeOnboarding,
  ])

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [menuLocation, setMenuLocation] = useState({ xAxis: 0, yAxis: 0 })
  const onMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setMenuLocation({ xAxis: e.clientX, yAxis: e.clientY })
    setIsMenuOpen(!isMenuOpen)
  }
  const isCreatingChannel = hallType === 'CHANNEL'
  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitleConfirm
          title={
            isCreatingChannel
              ? t('createHall.createChannel')
              : t('createHall.createGroup')
          }
          confirmDisabled={!title}
          onBack={closeCreateHall}
          onConfirm={onConfirmHandle}
        />
      }
    >
      <SettingsSection
        sx={{
          padding: '14px 20px 20px',
        }}
      >
        <>
          <WithEditableAvatar
            onSelectClick={async e => {
              if (!imageFileId) {
                await getFileFromUser(
                  'image/*',
                  file => {
                    showEditProfileDialog({
                      file: file as File,
                      onCancel: closeCurrentModal,
                      setFile: async (editedFile: File) => {
                        await uploadImage(editedFile, ({ fileId }) => {
                          setImageFileId(fileId)
                        })
                        closeCurrentModal()
                      },
                      onResetCb: closeCurrentModal,
                    })
                  },
                  false,
                )
              } else onMenuClick(e)
            }}
          >
            <StyledAvatar src={getImageUrl(imageFileId)} onClick={nothing}>
              {hallType === 'GROUP' ? (
                <WGroupIcon sx={{ width: '42px', height: '42px' }} />
              ) : (
                <WChannelIcon sx={{ width: '42px', height: '42px' }} />
              )}
              {/* <StyledPhotoCameraIcon /> */}
            </StyledAvatar>
          </WithEditableAvatar>
          {isMenuOpen && (
            <ProfilePictureMenu
              showDelete={!!imageFileId}
              onSubmit={setImageFileId}
              onClose={() => setIsMenuOpen(false)}
              xAxis={menuLocation.xAxis}
              yAxis={menuLocation.yAxis}
            />
          )}

          <Box sx={{ marginTop: '23px' }}>
            <Label
              title={
                isCreatingChannel
                  ? t('profile.hall.channelName')
                  : t('profile.hall.groupName')
              }
              required
            />
            <TextInput
              onChange={setTitle}
              value={title}
              placeholder={
                isCreatingChannel
                  ? t('profile.hall.enterChannelName')
                  : t('profile.hall.enterGroupName')
              }
              maxLength={hallConstraints.name.maxLength}
            />

            <Label title={t('profile.hall.description')} />
            <MultilineInput
              maxLength={hallConstraints.description.maxLength}
              placeholder={
                isCreatingChannel
                  ? t('profile.hall.enterChannelDescription')
                  : t('profile.hall.enterGroupDescription')
              }
              value={description}
              onChange={setDescription}
              showMaxLength
            />

            {workspaceId && isCreatingChannel && (
              <ErrorBox>
                <ErrorOutlineIcon
                  fontSize="small"
                  sx={{ color: 'neutral500' }}
                />
                <Typography color="neutral700" variant="paragraph3">
                  {t('createHall.createInWorkspace')}
                </Typography>
              </ErrorBox>
            )}
          </Box>
        </>
      </SettingsSection>
    </PageWithAppBar>
  )
}

const ErrorBox = styled('div')`
  margin-top: 8px;
  display: flex;
  align-items: start;
  gap: 4px;
`
